import cn from 'classnames'
import { AllHTMLAttributes, CSSProperties, ElementType, MouseEvent, forwardRef, ReactNode, isValidElement } from 'react'

import styles from './Tag.module.scss'

import Button from '~elements/Button'
import CloseIcon from '~icons/close-small.svg'
import HashtagSmallIcon from '~icons/hashtag-small.svg'

export type TagProps = Prefer<
  {
    as?: ElementType
    label?: string
    color?: string | string[]
    size?: 'xs' | 'sm' | 'md'
    variant?: 'light' | 'dark' | 'transparent'
    icon?: 'circle' | 'hash' | ReactNode
    bordered?: boolean
    value?: unknown
    onRemove?: (value: unknown, ev: MouseEvent<HTMLButtonElement>) => void
  },
  AllHTMLAttributes<HTMLElement>
>

export const DEFAULT_TAG_COLOR = '#8489FF'

const Tag = forwardRef<HTMLElement, TagProps>(
  (
    {
      as: Element = 'span',
      label,
      color,
      size = 'sm',
      variant = 'dark',
      icon = 'circle',
      bordered = false,
      className,
      type = 'button',
      value,
      disabled,
      onRemove,
      ...props
    },
    ref
  ) => {
    const renderIcon = (color = DEFAULT_TAG_COLOR, key?: string | number) => (
      <span key={key} className={styles.icon} style={{ color } as CSSProperties}>
        {icon === 'circle' ? <span className={styles.circle} /> : <HashtagSmallIcon className={styles.hash} />}
      </span>
    )

    return (
      <Element
        ref={ref}
        disabled={disabled}
        type={Element === 'button' ? type : null}
        className={cn(
          styles.tag,
          styles[size],
          styles[variant],
          typeof icon === 'string' && styles[icon],
          bordered && styles.bordered,
          className
        )}
        {...props}
      >
        {isValidElement(icon) && <span className={styles.icon}>{icon}</span>}

        {!isValidElement(icon) && Array.isArray(color) && (
          <span className={styles.stack}>{color.map((color, i) => renderIcon(color, i))}</span>
        )}

        {!isValidElement(icon) && color && !Array.isArray(color) && renderIcon(color)}

        {label && <span className={styles.label}>{label}</span>}

        {!disabled && onRemove && (
          <Button
            role="none"
            tabIndex={-1}
            className={styles.remove}
            variant="plain"
            title="Remove"
            icon={<CloseIcon />}
            onClickCapture={(e) => {
              e.persist()
              e.stopPropagation()
              onRemove(value, e)
            }}
          />
        )}
      </Element>
    )
  }
)

export default Tag
